import { Inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AccountDto, GosuLoginModel } from '@ay-gosu-party/server-shared';
import { DialogService } from '@ay-gosu/ui/dialog';
import {
  GosuLoginConfigDto,
  IBackstageLoginService,
} from '@ay-gosu/ui/login/login.service';
import { ExposedPromise } from '@ay/util/exposed-promise';
import { jwtDecode } from 'jwt-decode';
import {
  Observable,
  ReplaySubject,
  combineLatestWith,
  filter,
  firstValueFrom,
  map,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { ConnectionService } from '../connection.service';

export enum LoginStatus {
  // 還沒有處理自動登入
  INIT,
  // 未登入(已經處理完自動登入)
  NOT_LOGGED_IN,
  // 已登入，但尚未選擇組織
  NOT_CHOSEN_COMPANY,
  // 已登入，且已選擇組織
  LOGGED,
}

export const INIT = LoginStatus.INIT;
export const NOT_LOGGED_IN = LoginStatus.NOT_LOGGED_IN;
export const NOT_CHOSEN_COMPANY = LoginStatus.NOT_CHOSEN_COMPANY;
export const LOGGED = LoginStatus.LOGGED;

@Injectable({ providedIn: 'root' })
export class BackstageLoginService implements IBackstageLoginService {
  // 儲存登入後的 JWT
  // 如果是 null 則尚未登入
  // 儘管有值時，有可能還沒選擇組織
  public token$ = new ReplaySubject<string | null>(1);

  // 如果需要沒有組織的 JWT，可以使用這個
  public payload$ = this.token$.pipe(
    map((token) => {
      if (!token) return null;

      return jwtDecode(token) as AccountDto;
    }),
    shareReplay(1),
  );

  // 登入後 JWT 被解開的內容
  public payload = toSignal(this.payload$, { initialValue: null });

  // 登入後 JWT 被解開的內容
  // 儘管有值時，一定會是已經選擇組織的狀態
  public account$ = this.payload$.pipe(
    map((payload) => {
      if (!payload) return null;
      return payload.companyId ? payload : null;
    }),
    shareReplay(1),
  );

  // 帳號的 signal 版本，有可能是 null
  public account = toSignal(this.account$, { initialValue: null });

  // 登入的狀態
  public status$ = this.payload$.pipe(
    combineLatestWith(this._connectionService.status$),
    map(([account, connectionStatus]) => {
      if (connectionStatus !== '連線至伺服器') return INIT;

      if (!account) return NOT_LOGGED_IN;

      if (!account.companyId) return NOT_CHOSEN_COMPANY;

      return LOGGED;
    }),
    startWith(INIT),
  );

  // 確認是否已經登入的 signal
  public isLogged = toSignal(
    this.status$.pipe(map((status) => status === LOGGED)),
    { initialValue: false },
  );

  // 與 GOSU 登入相關的設定
  public config$ = this._connectionService.connected$.pipe(
    switchMap(() => GosuLoginModel.fetchConfig()),
    shareReplay(1),
  );

  public config = toSignal(this.config$, {
    initialValue: null,
  });

  public companyList$ = this.payload$.pipe(
    switchMap((account) => {
      if (!account) return [];

      return GosuLoginModel.fetchCompanyList();
    }),
  );

  public companyList = toSignal(this.companyList$, { initialValue: [] });

  public afterAutoLogin = new ExposedPromise<void>();

  public constructor(
    private readonly _connectionService: ConnectionService,
    private readonly _router: Router,
    private readonly _dialogService: DialogService,
    @Inject('LOCAL_STORAGE_KEY')
    private readonly _localStorageKey: string,
  ) {
    this.storeTokenToLocalStorage();

    // 當連線完畢後，處理自動登入機制
    this._connectionService.connected$
      .pipe(
        filter((status) => status),
        map(async () => {
          if (!this.afterAutoLogin.isPending()) {
            this.afterAutoLogin = new ExposedPromise<void>();
          }

          try {
            await this.processAutoLogin();
            this.afterAutoLogin.resolve();
          } catch (error) {
            console.error(error);
            this.afterAutoLogin.reject(error);
          }
        }),
      )
      .subscribe();
  }

  public async createCompany(companyName: string): Promise<string> {
    return await GosuLoginModel.createCompany(companyName);
  }

  public async updateCompanyName(companyName: string): Promise<void> {
    await GosuLoginModel.updateCompanyName(companyName);
  }

  public async loginViaToken(token: string): Promise<void> {
    const renewed = await GosuLoginModel.refresh(token);
    this.token$.next(renewed);

    await this.afterLogin();
  }

  public async verifyInviteToken(token: string): Promise<{ email: string }> {
    return await GosuLoginModel.verifyInviteToken(token);
  }

  public async fetchPasswordDefaultRules(): Promise<
    { type: string; args: any }[]
  > {
    return await GosuLoginModel.fetchPasswordDefaultRules();
  }

  public async register(
    name: string,
    mail: string,
    password: string,
  ): Promise<void> {
    return await GosuLoginModel.register(name, mail, password);
  }

  public async registerViaInvite(
    token: string,
    name: string,
    password: string,
  ): Promise<void> {
    await GosuLoginModel.registerViaInvite(token, name, password);
  }

  public async sendVerifyMail(): Promise<void> {
    await GosuLoginModel.sendVerifyMail();
  }

  public async changePassword(
    oldPassword: string,
    password: string,
  ): Promise<void> {
    await GosuLoginModel.changePassword(oldPassword, password);
  }

  public async changePasswordByToken(
    token: string,
    password: string,
  ): Promise<void> {
    await GosuLoginModel.changePasswordByToken(token, password);
  }

  public async verifyResetPasswordToken(token: string): Promise<void> {
    await GosuLoginModel.verifyResetPasswordToken(token);
  }

  public async sendVerifySms(
    countryCode: string,
    phone: string,
  ): Promise<{ result: boolean; remainingCount: number }> {
    return await GosuLoginModel.sendVerifySms(countryCode, phone);
  }

  public async verifySms(
    countryCode: string,
    phone: string,
    code: string,
  ): Promise<string> {
    return await GosuLoginModel.verifySmsCode(countryCode, phone, code);
  }

  public async updateName(name: string): Promise<void> {
    await GosuLoginModel.updateName(name);
  }

  public async sendResetPasswordMail(email: string): Promise<void> {
    await GosuLoginModel.sendResetPasswordMail(email);
  }

  public lineLogin(): Observable<{
    status: string;
    state: string;
    info?: { name: string; picture: string; email: string };
    code?: string;
    error?: string;
    description?: string;
    url?: string;
  }> {
    return GosuLoginModel.lineLogin();
  }

  public async loginViaLine(): Promise<string> {
    const token = await GosuLoginModel.loginViaLine();
    return token;
  }

  public async registerViaLine(): Promise<string> {
    return await GosuLoginModel.registerViaLine();
  }

  public async bindLine(): Promise<void> {
    await GosuLoginModel.bindLine();
  }

  public async unbindLine(): Promise<void> {
    await GosuLoginModel.unbindLine();
  }

  public async loginViaFb(accessToken: string): Promise<string> {
    const token = await GosuLoginModel.loginViaFb(accessToken);
    return token;
  }

  public async registerViaFb(
    accessToken: string,
    name: string,
  ): Promise<string> {
    return await GosuLoginModel.registerViaFb(accessToken, name);
  }

  public async bindFb(accessToken: string): Promise<void> {
    await GosuLoginModel.bindFb(accessToken);
  }

  public async unbindFb(): Promise<void> {
    await GosuLoginModel.unbindFb();
  }

  public async loginViaGoogle(credential: string): Promise<string> {
    const token = await GosuLoginModel.loginViaGoogle(credential);
    return token;
  }

  public async registerViaGoogle(credential: string): Promise<string> {
    return await GosuLoginModel.registerViaGoogle(credential);
  }

  public async bindGoogle(credential: string): Promise<void> {
    await GosuLoginModel.bindGoogle(credential);
  }

  public async unbindGoogle(): Promise<void> {
    await GosuLoginModel.unbindGoogle();
  }

  public fetchConfig(): Promise<GosuLoginConfigDto> {
    return firstValueFrom(this.config$);
  }

  public async login(email: string, password: string): Promise<void> {
    const token = await GosuLoginModel.login(email, password);
    this.token$.next(token);

    await this.afterLogin();
  }

  public async afterLogin() {
    const account = await firstValueFrom(this.payload$);
    const config = await firstValueFrom(this.config$);

    if (config.email.enabled && !account?.emailVerified) {
      this._router.navigate(['backstage', 'login', 'verify-email'], {
        queryParams: { status: 'NOT_YET' },
      });
      return;
    }

    if (config.sms.enabled && !account?.phoneVerified) {
      this._router.navigate(['backstage', 'login', 'phone-verify'], {
        queryParams: { status: 'NOT_YET' },
      });
      return;
    }

    const companyList = await firstValueFrom(this.companyList$);
    switch (companyList.length) {
      // 如果沒有組織，建立組織
      case 0:
        this._router.navigate(['backstage', 'login', 'create-company']);
        break;

      // 如果只有一個組織，選擇該組織
      case 1:
        await this.selectCompany(companyList[0].id);
        break;

      // 如果有多個組織，選擇組織
      default:
        this._router.navigate(['backstage', 'login', 'select-company']);
        break;
    }
  }

  public async selectCompany(companyId: number) {
    try {
      const token = await GosuLoginModel.selectCompany(companyId);
      this.token$.next(token);
      this._router.navigate(['backstage', 'event']);
    } catch (error: any) {
      if (error?.code === 'NOT_PASS_PASSWORD_RULES') {
        await this._dialogService.failure('密碼設定不安全', {
          disableClose: true,
          messages: ['您目前的密碼不符合密碼規則，請重新設定'],
          buttons: [
            {
              text: '重設密碼',
              color: 'warn',
              click: (dialogRef) => {
                dialogRef.close();
                this._router.navigate(
                  ['backstage', 'login', 'reset-password'],
                  {
                    queryParams: { companyId },
                  },
                );
              },
            },
          ],
        });
      } else {
        console.error(error);
      }
    }
  }

  public async logout() {
    await GosuLoginModel.logout();
    this.token$.next(null);
    this._router.navigate(['backstage', 'login', 'form']);
  }

  public async refresh() {
    const token = await firstValueFrom(this.token$);
    if (!token) return;

    const renewed = await GosuLoginModel.refresh(token);
    this.token$.next(renewed);
  }

  public async processAutoLogin() {
    const token = localStorage.getItem(this._localStorageKey);

    if (token) {
      try {
        this.token$.next(null);
        const renewedToken = await GosuLoginModel.refresh(token);
        this.token$.next(renewedToken);
      } catch (error) {
        this.token$.next(null);
      }
    } else {
      this.token$.next(null);
    }
  }

  public storeTokenToLocalStorage() {
    this.token$.subscribe((token) => {
      if (!token) {
        localStorage.removeItem(this._localStorageKey);
      } else {
        localStorage.setItem(this._localStorageKey, token);
      }
    });
  }
}
